import {
  type EvidenceUploadMode,
  type ProcessUploadResponse,
  type UploadEvidenceData,
  UseMutationOptions,
  useUploadEvidence,
} from 'api';
import { logger } from 'monitor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNetworkStatus } from '../../../network-status';
import { useOfflineEvidenceStore } from '../../stores/useOfflineEvidenceStore';
import { buildEvidenceMetadataFromExif, extractExifData } from '../../utils/exif/exif';

export interface OfflinePhoto {
  id: string;
  url: string;
}

export interface OfflineProcessUploadResponse {
  created: string;
  id: string;
  image: string;
  message: string;
  status: 'pending' | 'success' | 'error';
}

interface SyncResult {
  offlineId: string;
  success: boolean;
  result?: ProcessUploadResponse;
  error?: Error;
}

interface SyncComplete {
  message: string;
  results: SyncResult[];
  success: boolean;
}
/**
 * @deprecated
 */
export const useOfflineEvidence = ({
  parentId,
  mode,
  mutationOptions,
  onSyncComplete,
}: {
  parentId: string | number;
  mode: EvidenceUploadMode;
  mutationOptions?: UseMutationOptions<ProcessUploadResponse, unknown, UploadEvidenceData, unknown>;
  onSyncComplete?: (results: SyncResult[]) => void;
}) => {
  const { isOnline, isConnectedToInternet, setMode } = useNetworkStatus();
  const stringParentId = String(parentId);

  const {
    getPhotoObjectUrlsByParentId,
    cleanupObjectUrls,
    evidenceFiles: allEvidenceFiles,
    addEvidenceFile,
    removeEvidenceFile,
    getPhotoFromOfflineFile,
  } = useOfflineEvidenceStore();

  const evidenceFiles = useMemo(
    () => allEvidenceFiles.filter((file) => String(file.parentId) === stringParentId),
    [allEvidenceFiles, stringParentId]
  );

  const [offlinePhotos, setOfflinePhotos] = useState<OfflinePhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const loadingRef = useRef(false);

  const totalUploadProgress = useMemo(() => {
    const progressValues = Object.values(uploadProgress);
    if (progressValues.length === 0) return 0;
    return progressValues.reduce((sum, progress) => sum + progress, 0) / progressValues.length;
  }, [uploadProgress]);

  const { mutateAsync: uploadEvidence, ...uploadRest } = useUploadEvidence(mode, mutationOptions);

  const loadOfflinePhotos = useCallback(async () => {
    if (loadingRef.current) {
      logger.debug('Load already in progress, skipping', { parentId: stringParentId });
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    logger.info('Loading offline photos', { parentId: stringParentId });

    try {
      const photos = await getPhotoObjectUrlsByParentId(stringParentId);
      logger.info('Loaded offline photos successfully', {
        count: photos.length,
        parentId: stringParentId,
        photoIds: photos.map((p) => p.id),
      });
      setOfflinePhotos(photos);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load offline photos');
      logger.error('Failed to load offline photos', error, { parentId: stringParentId });
      setError(error);
      setOfflinePhotos([]);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
      logger.debug('Load offline photos completed', { parentId: stringParentId });
    }
  }, [getPhotoObjectUrlsByParentId, stringParentId]);

  useEffect(() => {
    loadOfflinePhotos();

    return () => {
      cleanupObjectUrls(stringParentId);
    };
  }, [stringParentId, cleanupObjectUrls, loadOfflinePhotos]);

  useEffect(() => {
    if (!loadingRef.current) {
      // Add a small delay to ensure the store has been updated
      const timer = setTimeout(() => {
        loadOfflinePhotos();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [allEvidenceFiles.length, loadOfflinePhotos]);

  const uploadEvidenceWithOfflineSupport = useCallback(
    async (data: UploadEvidenceData) => {
      const uploadId = `${data.parentId}-${data.photo.name}-${Date.now()}`;

      logger.info('Starting evidence upload', {
        evidenceType: data.evidenceType,
        fileName: data.photo.name,
        fileSize: data.photo.size,
        isOnline,
        parentId: data.parentId,
        uploadId,
      });

      if (isOnline) {
        try {
          logger.debug('Extracting EXIF data', { fileName: data.photo.name, uploadId });
          const exifData = await extractExifData(data.photo);

          logger.info('Uploading evidence online', {
            fileName: data.photo.name,
            hasExifDeviceName: !!exifData.deviceName,
            hasExifMake: !!exifData.make,
            hasExifModel: !!exifData.model,
            hasExifTimestamp: !!exifData.timestamp,
            uploadId,
          });

          const result = await uploadEvidence({
            ...data,
            metadata: buildEvidenceMetadataFromExif(exifData, data.metadata),
            onProgress: (progress) => {
              logger.debug('Upload progress', { progress, uploadId });
              setUploadProgress((prev) => ({ ...prev, [uploadId]: progress }));
            },
          });

          setUploadProgress((prev) => {
            const { [uploadId]: _, ...rest } = prev;
            return rest;
          });

          logger.info('Evidence uploaded successfully', {
            fileName: data.photo.name,
            result,
            uploadId,
          });

          return result;
        } catch (error) {
          logger.error('Failed to upload evidence online', error, {
            fileName: data.photo.name,
            parentId: data.parentId,
            uploadId,
          });

          setUploadProgress((prev) => {
            const { [uploadId]: _, ...rest } = prev;
            return rest;
          });
          throw error;
        }
      } else {
        logger.info('Device is offline, storing evidence for later upload', {
          evidenceType: data.evidenceType,
          fileName: data.photo.name,
          fileSize: data.photo.size,
          parentId: data.parentId,
          uploadId,
        });

        const offlineId = await addEvidenceFile(data);

        logger.info('Evidence stored offline successfully', {
          fileName: data.photo.name,
          offlineId,
          uploadId,
        });

        // This helps prevent race conditions
        await new Promise((resolve) => setTimeout(resolve, 50));
        await loadOfflinePhotos();

        return {
          created: new Date().toISOString(),
          id: offlineId,
          image: '',
          message: 'Stored offline',
          status: 'pending',
        } as OfflineProcessUploadResponse;
      }
    },
    [isOnline, uploadEvidence, addEvidenceFile, loadOfflinePhotos]
  );

  const syncOfflineEvidence = useCallback(async (): Promise<SyncComplete> => {
    logger.info('Starting offline evidence sync', {
      evidenceFilesCount: evidenceFiles.length,
      isOnline,
      parentId: stringParentId,
    });

    if (!isConnectedToInternet) {
      logger.warn('Cannot sync offline evidence while offline', { parentId: stringParentId });
      return { message: 'Device is offline', results: [], success: false };
    }

    if (!isOnline) {
      logger.info("Switching mode to 'auto' during offline evidence sync because the app is currently offline.", {
        parentId: stringParentId,
      });
      setMode('auto');
    }

    setIsSyncing(true);
    setError(null);

    const results: SyncResult[] = [];

    try {
      const filesToSync = evidenceFiles;
      if (filesToSync.length === 0) {
        logger.info('No offline evidence to sync', { parentId: stringParentId });
        setIsSyncing(false);
        return { message: 'No offline evidence to sync', results: [], success: true };
      }

      logger.info(`Syncing ${filesToSync.length} offline evidence files`, {
        fileIds: filesToSync.map((f) => f.offlineId),
        parentId: stringParentId,
      });

      await Promise.allSettled(
        filesToSync.map(async (offlineFile) => {
          const uploadId = `sync-${offlineFile.offlineId}`;

          logger.info('Syncing offline file', {
            fileName: offlineFile.photoMeta.name,
            fileSize: offlineFile.photoMeta.size,
            offlineId: offlineFile.offlineId,
            uploadId,
          });

          try {
            const photo = await getPhotoFromOfflineFile(offlineFile.offlineId);
            if (!photo) {
              throw new Error(`Failed to retrieve photo for offline file ${offlineFile.offlineId}`);
            }

            logger.debug('Retrieved photo from offline file', {
              offlineId: offlineFile.offlineId,
              photoSize: photo.size,
              photoType: photo.type,
            });

            const uploadData: UploadEvidenceData = {
              evidenceType: offlineFile.evidenceType,
              metadata: offlineFile.metadata,
              model: offlineFile.model,
              onProgress: (progress) => {
                logger.debug('Sync upload progress', { progress, uploadId });
                setUploadProgress((prev) => ({ ...prev, [uploadId]: progress }));
              },
              parentId: offlineFile.parentId,
              photo,
              startTime: Date.now(),
            };

            const result = await uploadEvidence(uploadData);

            logger.info('Offline file synced successfully', {
              fileName: offlineFile.photoMeta.name,
              offlineId: offlineFile.offlineId,
              result,
            });

            await removeEvidenceFile(offlineFile.offlineId);

            logger.info('Offline file removed after successful sync', {
              offlineId: offlineFile.offlineId,
            });

            results.push({ offlineId: offlineFile.offlineId, result, success: true });
          } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to upload offline evidence');
            logger.error('Failed to sync offline evidence file', error, {
              fileName: offlineFile.photoMeta.name,
              fileSize: offlineFile.photoMeta.size,
              offlineId: offlineFile.offlineId,
            });

            results.push({ error, offlineId: offlineFile.offlineId, success: false });
          } finally {
            setUploadProgress((prev) => {
              const { [uploadId]: _, ...rest } = prev;
              return rest;
            });
          }
        })
      );

      const successful = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      logger.info(`Sync completed`, {
        failed,
        failedIds: results.filter((r) => !r.success).map((r) => r.offlineId),
        parentId: stringParentId,
        successful,
        successfulIds: results.filter((r) => r.success).map((r) => r.offlineId),
        total: results.length,
      });

      await loadOfflinePhotos();

      return {
        message: `Synced ${successful} files${failed > 0 ? `, ${failed} failed` : ''}`,
        results,
        success: failed === 0,
      };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to sync offline evidence');
      logger.error('Sync process failed', error, {
        parentId: stringParentId,
        resultsCount: results.length,
      });
      setError(error);
      return { message: error.message, results, success: false };
    } finally {
      setIsSyncing(false);
      setUploadProgress({});
      logger.info('Sync process completed', {
        parentId: stringParentId,
        resultsCount: results.length,
      });
      onSyncComplete?.(results);
    }
  }, [
    isOnline,
    evidenceFiles,
    stringParentId,

    getPhotoFromOfflineFile,
    uploadEvidence,
    removeEvidenceFile,
    loadOfflinePhotos,
    onSyncComplete,
    isConnectedToInternet,
    setMode,
  ]);

  const clearOfflineEvidence = useCallback(async () => {
    const offlinePhotoIds = evidenceFiles.map((file) => file.offlineId);

    logger.info('Starting to clear offline evidence', {
      count: offlinePhotoIds.length,
      offlineIds: offlinePhotoIds,
    });

    setIsClearing(true);
    setError(null);

    try {
      await Promise.all(offlinePhotoIds.map((id) => removeEvidenceFile(id)));
      logger.info('Cleared offline evidence successfully', {
        count: offlinePhotoIds.length,
        offlineIds: offlinePhotoIds,
        parentId: stringParentId,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to clear offline evidence');
      logger.error('Failed to clear offline evidence', error, {
        count: offlinePhotoIds.length,
        offlineIds: offlinePhotoIds,
        parentId: stringParentId,
      });
      setError(error);
      throw error;
    } finally {
      setIsClearing(false);
      logger.info('Clear offline evidence process completed', {
        count: offlinePhotoIds.length,
        parentId: stringParentId,
      });
    }
  }, [evidenceFiles, removeEvidenceFile, stringParentId]);

  return {
    clearOfflineEvidence,
    isClearing,
    isSyncing,
    mutateAsync: uploadEvidenceWithOfflineSupport,
    offlineError: error,
    offlineIsLoading: isLoading,
    offlinePhotos,
    refetchOfflinePhotos: loadOfflinePhotos,
    removeEvidenceFile,
    syncOfflineEvidence,
    uploadProgress: totalUploadProgress,
    ...uploadRest,
  };
};
